<template>
  <div class="cartItem" v-if="!placeholder && Object.keys(itemEnrichData).length && !itemUpdating && !itemDowngrading" :data-bundle-child-keys="item.bundleChildKeys" 
  :data-bundle-child-quantities="item.bundleChildQuantities" 
  :data-bundle-id="item.itemBundleID" 
  :data-bundle-parent-key="item.bundleParentKey">
    <div class="cartItem__img">
      <div v-if="itemEnrichData?.is_subscription || item.properties.subscription_id" class="cartItem__sub--badge hide"></div>
      <a :href="`${siteRoutes}products/${item.handle}?variant=${item.variant_id}`">
        <ImageField class="cartItem__image" :obj="{'url':item.image.url,'alt':item.image.alt,'width':256,'height': 256}" width-param="width" sizes="(max-width: 767px) 80px, 128px"/>   
      </a>
    </div>
      
    <div class="cartItem__info">
      
      <div class="cartItem__details">
        <a v-if="itemEnrichData?.title" class="cartItem__title" :href="`${siteRoutes}products/${item.handle}?variant=${item.variant_id}`">
          {{ itemEnrichData?.title  }}
        </a>
        <a v-else class="cartItem__title" :href="`${siteRoutes}products/${item.handle}?variant=${item.variant_id}`">
          {{ item.product_title  }}
        </a>
        <div v-if="itemEnrichData?.subtitle" class="cartItem__subtitle hide">{{ itemEnrichData?.subtitle }}</div>
        <div v-if="variantTitle" class="cartItem__variantTitle">{{ variantTitle }}</div>
        <div v-if="item.bundleItemList" v-html="item.bundleItemList" class="cartItem__bundleItems"></div>
      </div>

      <div class="cartItem__price-wrapper">
        
        <div v-if="isDiscountedPrice" data-is-discounted-price class="cartItem__price">
          <span>{{ formatPrice(item.itemPrice) }}</span>
          <span class="cartItem__price-compare">{{ comparePrice }}</span>
        </div>
        <div v-else-if="!isDiscountedPrice && isBundleParent" data-no-discounted-price data-is-bundle-parent class="cartItem__price">
          <span>{{ formatPrice(item.itemPrice) }}</span>
          <span v-if="(item.itemCompareAtPrice !== null) && item.itemPrice < item.itemCompareAtPrice" class="cartItem__price-compare">{{  formatPrice(item.itemCompareAtPrice)  }}</span>
        </div>
        <div v-else>
          {{ formatPrice(item.price) }}
        </div>
        
      </div>

    <div class="cartItem__actions-wrapper">
      <div class="cartItem__actions">
          <button type="button" :disabled="isGiftWithPurchase" aria-label="Decrease cart item quantity" class="cartItem__action" @click.prevent="updateQty(item.line, item.quantity - 1, false, $event)">
            <svg class="icon icon--xs">
              <use href="#minus"></use>
            </svg>
          </button>
          <span :class="{'cartItem--processing': updating}" class="cartItem__qty" v-text="item.quantity"></span>
          <button type="button" :disabled="isGiftWithPurchase" aria-label="Increase cart item quantity" class="cartItem__action" @click.prevent="updateQty(item.line, item.quantity + 1, false, $event)">
            <svg class="icon icon--xs">
              <use href="#plus"></use>
            </svg>
          </button>
      </div>

      <div v-if="!isGiftWithPurchase && !itemUpdating && !itemDowngrading"
          @click.prevent="updateQty(item.line, 0, false, $event)"
          class="cartItem__remove">
          <svg class="icon icon--sm"><use href="#trash"></use></svg>
      </div>

      <div v-if="itemDiscountMessage" class="cartItem__discount-msg">{{ itemDiscountMessage }}</div>

      <div v-if="isPreOrder" class="cartItem__discount-msg">Pre-order</div>
    </div>

    <div v-if="shopLocation !== 'GB' && hasSubscription && !isGiftWithPurchase && !isPreOrder" class="cartItem__frequency">
      <div v-if="itemEnrichData?.is_subscription || item.selling_plan_allocation?.selling_plan">

        <select v-if="!itemUpdating && !itemDowngrading" :disabled="itemUpdating" class="cartItem__frequency-selector"
          v-model="itemEnrichData.selling_plan_id"
          @change="updateDeliveryFrequency($event)"
        >
          <optgroup label="Pay Full Price">
            <option value="onetime">One-Time Only</option>
          </optgroup>

          <optgroup label="Subscribe &amp; Save">
            <option
              v-for="selling_plan in filterSellingPlans" 
              :value="selling_plan.id"
            >
              Delivery every {{ selling_plan.name }}
            </option>
          </optgroup>

        </select>
        <svg class="icon icon--sm caret"><use href="#caret-down"></use></svg>
        <span v-if="itemDowngrading" class="cartItem__frequency-change active">Downgrading...</span>
        <span v-if="itemUpdating" class="cartItem__frequency-change active">Updating...</span>
      </div>
      <div v-if="!itemEnrichData?.is_subscription && hasSubscription && !itemDowngrading && !isPreOrderInDescription">
        <div @click="($event) => { itemUpdating = true; upgradeToSubscription($event) }" :class="['cartItem__frequency-change', {'active': itemUpdating}]">
          <svg class="cartItem__frequency-change-radio icon icon--sm"><use href="#checkbox-empty"></use></svg>
          <span v-if="!itemUpdating">Subscribe and save</span>
          <span v-else>Upgrading...</span>
        </div>
      </div>
    </div>      
    </div>


  </div>
  <div v-else class="cartItem cartItem__placeholder">
    <div class="cartItem__placeholder-image placeholder"></div>
    <div class="cartItem__placeholder-wrap">
      <div class="cartItem__placeholder-title placeholder"></div>
      <div class="cartItem__placeholder-variant placeholder"></div>
      <div class="cartItem__placeholder-actions placeholder"></div>
      <div class="cartItem__placeholder-frequency placeholder"></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, toRefs, computed, watchEffect } from 'vue';
  import {useStore} from "vuex";
  import cartHelpers from '../api';
  import ImageField from '../../global/image.vue'

  const props = defineProps({
    cart: {
      type: Array,
      default: () => ([])
    },

    item: {
      type: Object,
      default: () => ({})
    },

    placeholder: {
      type: Boolean,
      default: false
    },
  })

  const { item } = toRefs(props)
  const { status } = cartHelpers;
  const store = useStore();

  const shopLocation = ref(window.shopLocation);
  const itemDowngrading = ref(false);
  const itemUpdating = ref(false);
  const multiPackOptions = ['boxes', 'tubs', 'bottles'];
  const cart = computed(() => store.state.cart)
  const enrichData = computed(() => store.state.extraData);
  const cartStatus = computed(() => store.state.status)
  const updateLine = computed(() => store.state.updateLine)

  const isPreOrderInDescription = computed(() => {
    let productDescription = item.value.product_description;

    if (typeof productDescription !== 'undefined' && productDescription !== null && productDescription !== '') {
      productDescription = productDescription.toLowerCase();
      return (productDescription.indexOf('pre-order') > -1);
    } else {
      return false;
    }
  });

  const updating = computed(() => {
    if (cartStatus.value !== status.progress) return false;

    const update = updateLine.value;

    if (update && typeof update === 'object' && !Array.isArray(update)) {
      return item.value.key && (item.value.key in update);
    }

    return update != null && update == item.value.line;
  });

  /*watchEffect(() => {
    console.log('Add to cart debug:', {
      updateLine: updateLine.value,
      type: typeof updateLine.value,
      itemKey: item.value.key,
      itemLine: item.value.line,
      target: item.value.key ?? item.value.line,
      updating: updating.value
    });
  })*/;

  const itemEnrichData = computed(() => {
    // Ensure item.value and item.value.key exist before accessing
    if (!item?.value?.key) {
      console.warn('Item or item key is undefined or null');
      return {};
    }

    const key = item.value.key;

    // Find matching variant in enrichData
    const variant = enrichData.value.find(variant => variant.key === key);

    // Return found variant or empty object if undefined
    return variant || {};
  });

  const itemDiscountMessage = computed(() => {
    if (item.value.discounts.length > 0) {
      return item.value.discounts[0].title;
    } else {
      return false;
    }
  })

  const variantTitle = computed(() => {
    let title = item.value.variant_title;

    if (item.value.product_type === "Bundle" || item.value.product_type !== "Monthly Box") {
      return title;
    }

    // Return early if title is null or undefined to avoid replace errors
    if (title == null) {
      return title;
    }

    // Replace numbers with "serving" or "servings" where appropriate
    title = title.replace(/\b(\d+)\b(?!\s*(Tub|Tubs|Bottle|Bottles|Box|Boxes))/g, match => {
      return match === "1" ? `${match} Serving` : `${match} Servings`;
    });

    // Remove " / One Time Purchase" or " | One Time Purchase"
    title = title.replace(/( \/|\|) One Time Purchase$/, '');

    return title;
  });

  const isGiftWithPurchase = computed(() => {
    return item.value.properties && item.value.properties._source && item.value.properties._source == 'Gift with Purchase'
  })

  const disabled = computed(() => {
    return itemEnrichData.value?.inventory_quantity <= item.value.quantity;
  })

  const hasSubscription = computed(() => {
    if(isSingleServing.value) return false;

    if(isMultiPack.value) return true;

    if (!itemEnrichData.value || !itemEnrichData.value.selling_plan_groups || !itemEnrichData.value.variant || !itemEnrichData.value.variant.selling_plan_allocations) {
      return false;
    }

    const groupIds = new Set(itemEnrichData.value.selling_plan_groups.map(group => group.id));
    return itemEnrichData.value.variant.selling_plan_allocations.some(allocation => groupIds.has(allocation.selling_plan_group_id));
  })

  const isPreOrder = computed(() => {
    return item.value.selling_plan_allocation?.selling_plan.name === "Pre-order";
  })

  const isMultiPack = computed(() => {
    return item.value.options_with_values.some(option => multiPackOptions.includes(option.name.toLowerCase()));
  })

  const isSingleServing = computed(() => {
    return (item.value.variant_options[1] == '1');
  })

  const oneTimeVariant = computed(() => {
    return itemEnrichData.value?.product_variants.filter(variant => {
      if (item.value.variant_options.length === 3) {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] === item.value.variant_options[1] && variant.options[2] === 'One Time Purchase';
      } else {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] === 'One Time Purchase';
      }
    });
  });

  const firstSubVariant = computed(() => {
    return itemEnrichData.value?.product_variants.filter(variant => {
      if (item.value.variant_options.length === 3) {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] === item.value.variant_options[1] && variant.options[2] != 'One Time Purchase';
      } else {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] != 'One Time Purchase';
      }
    });
  });

  const isDiscountedPrice = computed(() => {
    const { properties } = item.value;
    const { is_subscription, compare_at_price } = itemEnrichData.value;

    return (
      (properties && properties.selected_plan) ||
      is_subscription ||
      item.value.final_price < item.value.price ||
      (compare_at_price && compare_at_price > item.value.price)
    );
  });

  const isBundleParent = computed(() => {
    return item.value.isBundleParent;
  });

  const siteRoutes = computed(() => {
    const root = window.Shopify?.routes?.root || '/'
    return root
  });

  const filterSellingPlans = computed(() => {
    const allSellingPlans = itemEnrichData.value?.variant_selling_plans || [];
    
    return allSellingPlans.filter((sellingPlan) => {
      const adjustmentValue = sellingPlan.price_adjustments[0]?.value;
      if (isBundleParent.value) {
        return adjustmentValue === 25;
      } else {
        return adjustmentValue === 20;
      }
    });
  });

  const comparePrice = computed(() => {
    const { selling_plan_allocation, final_price, price, itemPrice, itemCompareAtPrice } = item.value;
    const { compare_at_price, is_subscription } = itemEnrichData.value;

    if (itemPrice < itemCompareAtPrice) return formatPrice(itemCompareAtPrice); // Stay bundle prices
    if (isMultiPack.value) return formatPrice(compare_at_price);
    if (selling_plan_allocation?.selling_plan) return formatPrice(selling_plan_allocation.compare_at_price);
    if (is_subscription) return formatPrice(price);
    if (final_price < price) return formatPrice(price);
    if (compare_at_price && compare_at_price > price) return formatPrice(compare_at_price);

    return null; // Explicit return for clarity
  });

  const formatPrice = (price) => {
    return window.formattedPrice(price/100)
  }

  async function updateQty(line, quantity = 0, option = true, event) {
    if(quantity && item.value.quantity < itemEnrichData.value?.inventory_quantity || !option){
      //Add Stay Bundle logic
      const bundleParentElement = event.target.closest('[data-bundle-child-keys]');
      const bundleChildKeys = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-child-keys') : '';
      const bundleChildQuantities = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-child-quantities') : '';
      const isBundleParent = bundleChildKeys !== '' ? true : false;

      let formData = {};
      // If this is a bundle parent product
      if (isBundleParent) {
        const childKeysArray = bundleChildKeys.split(',');
        const childQuantitiesArray = bundleChildQuantities.split(',');
    
        childKeysArray.forEach((childKey, i) => {
            const currentChildQty = parseInt(childQuantitiesArray[i]);
            let newChildQty = quantity;
            if (newChildQty < 0) newChildQty = 0;
    
            formData[String(childKey)] = newChildQty;
        });
      }
      
      await changeItem(line, quantity, null, isBundleParent, formData)
    }
  }

  const upgradeToSubscription = async (event) => {
    const isMultiPackVariant = isMultiPack.value;
    const itemId = isMultiPackVariant
      ? firstSubVariant.value[0].id
      : item.value.id;

    const sellingPlanId = isMultiPackVariant
    ? firstSubVariant.value[0]?.selling_plan_allocations.find(
        (allocation) =>
          allocation.selling_plan_group_id ===
          itemEnrichData.value.selling_plan_groups[0].id
      )?.selling_plan_id
    : itemEnrichData.value.selling_plan_groups[0].selling_plans[0].id;

    //Start Stay Bundle logic
    const bundleParentElement = event.target.closest('[data-bundle-child-keys]');
    const bundleChildKeys = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-child-keys') : '';
    const bundleChildQuantities = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-child-quantities') : '';
    const bundleID = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-id') : '';
    const bundleParentKey = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-parent-key') : '';

    const isBundleParent = bundleChildKeys !== '' ? true : false;
    //End Stay Bundle logic

    if (
      isMultiPackVariant ||
      (itemEnrichData.value.variant.selling_plan_allocations &&
        itemEnrichData.value.variant.selling_plan_allocations.length > 0)
    ) {
      if (isBundleParent) {
        //Start Stay Bundle logic
        let plan = sellingPlanId,
        additionDataArray = {},
        removalData= {};

        const childKeysArray = bundleChildKeys.split(',');
        const childQuantitiesArray = bundleChildQuantities.split(',');

        let product_data = childKeysArray.map((childKey, i) => {
          const childIsBundleParent = childKey === bundleParentKey ? true : false;
          const currentChildQty = parseInt(childQuantitiesArray[i]);
          const childID = parseInt(childKey.split(':')[0]);

          removalData[childKey] = 0;
    
          return {
            quantity: currentChildQty, 
            id: childID, 
            selling_plan: plan,
            properties: {
                bundle_id: bundleID,
                bundle_parent: childIsBundleParent
            }
          }
        });

        additionDataArray = product_data;
        //End Stay Bundle logic

        try {
          await updateItem(removalData);
          await addMultipleItems(additionDataArray);
        } catch (error) {
            console.error("Error upgrading to subscription:", error);
        } finally {
            itemUpdating.value = false;
        }
        
      } else {
        try {
          await changeItem(item.value.line, 0);
          await addItem(itemId, item.value.line, item.value.properties, item.value.quantity, sellingPlanId);
        } catch (error) {
              console.error("Error upgrading to subscription:", error);
        } finally {
            itemUpdating.value = false;
        }
      }
    }
  };

  const updateDeliveryFrequency = async (event) => {
    const isOneTime = event.target.value === 'onetime';
    const updateAction = isOneTime ? downgradeToOneTime : updateProperties;

    //Start Stay Bundle logic
    const bundleParentElement = event.target.closest('[data-bundle-child-keys]');
    const bundleChildKeys = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-child-keys') : '';
    const bundleChildQuantities = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-child-quantities') : '';
    const bundleID = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-id') : '';
    const bundleParentKey = bundleParentElement ? bundleParentElement.getAttribute('data-bundle-parent-key') : '';

    const isBundleParent = bundleChildKeys !== '' ? true : false;
    
    if (isBundleParent) {
      let plan = event.target.value,
          additionDataArray = {},
          removalData= {};

      const childKeysArray = bundleChildKeys.split(',');
      const childQuantitiesArray = bundleChildQuantities.split(',');

      let product_data = childKeysArray.map((childKey, i) => {
        const childIsBundleParent = childKey === bundleParentKey ? true : false;
        const currentChildQty = parseInt(childQuantitiesArray[i]);
        const childID = parseInt(childKey.split(':')[0]);

        removalData[childKey] = 0;
  
        if (isOneTime) {
          return {
            quantity: currentChildQty, 
            id: childID, 
            properties: {
                bundle_id: bundleID,
                bundle_parent: childIsBundleParent
            }
          }
        } else {
            return {
              quantity: currentChildQty, 
              id: childID, 
              selling_plan: plan,
              properties: {
                  bundle_id: bundleID,
                  bundle_parent: childIsBundleParent
              }
            }
        }
      });

      additionDataArray = product_data;

      try {
        itemDowngrading.value = isOneTime;
        itemUpdating.value = !isOneTime;
        await updateItem(removalData);
        await addMultipleItems(additionDataArray);
      } finally {
        itemDowngrading.value = false;
        itemUpdating.value = false;
      }

      //End Stay Bundle logic
    } else {
      try {
      itemDowngrading.value = isOneTime;
      itemUpdating.value = !isOneTime;
      await updateAction(event.target.value);
      } finally {
        itemDowngrading.value = false;
        itemUpdating.value = false;
      }
    }
  };

  const downgradeToOneTime = async () => {
    try {
      await changeItem(item.value.line, 0);
      const variantId = isMultiPack.value ? oneTimeVariant.value[0].id : item.value.id;
      await addItem(variantId,item.value.line, item.value.properties, item.value.quantity, null);
    } catch (error) {
      console.error("Error downgrading to one-time:", error);
    }
  };

  const updateProperties = async (value) => {
    try {
      await changeItem(item.value.line, item.value.quantity, Number(value));
      await store.dispatch("init");
    } catch (error) {
      console.error("Error updating properties:", error);
    }
  };

  const addItem = async (id, line, properties, quantity, sellingPlan) => {
    const itemAddedToCart = cart.value.items?.find(item =>
      item.variant_id === Number(id) &&
      (!properties || JSON.stringify(item.properties) === JSON.stringify(properties)) &&
      ((!sellingPlan && !item.selling_plan_allocation) ||
        (sellingPlan && item.selling_plan_allocation?.selling_plan.id === Number(sellingPlan)))
    );

    await store.dispatch("add", {
      id,
      quantity,
      ...(sellingPlan && { selling_plan: sellingPlan }),
    });

    // if (!itemAddedToCart) {

    // } else {
    //   await changeItem(line, itemAddedToCart.quantity + quantity, sellingPlan);
    //   await store.dispatch("init");
    // }
  };

  const addMultipleItems = async (formData) => {
    await store.dispatch("add", formData);
  };

  const changeItem = async (line, quantity, sellingPlan, isBundleParent, formData) => {

    if (isBundleParent) {
      try {
        const response = await store.dispatch("update", formData);
        return response;
      } catch (error) {
        console.error("Error in cart update:", error);
        throw error;
      }
    } else {
      try {
        const response = await store.dispatch("change", {
          line: line,
          quantity: quantity,
          ...(sellingPlan && { selling_plan: sellingPlan }),
        });
        return response;
      } catch (error) {
        console.error("Error in cart change:", error);
        throw error;
      }
    }
  };

  const updateItem = async (formData) => {
    try {
      const response = await store.dispatch("update", formData);
      return response;
    } catch (error) {
      console.error("Error in cart update:", error);
      throw error;
    }
  };
</script>